import { isEmpty } from "@bodil/core/assert";
import { attribute, attributeGetter, Component } from "@bodil/dom/component";
import * as mdi from "@mdi/js";
import type { Role } from "aria-attribute-types";
import { html } from "lit";
import { customElement } from "lit/decorators.js";

import styles from "./icon.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "bc-icon": BcIcon;
    }
}

const missing = mdi.mdiHeartBroken;

const iconMap = {
    calculator: [mdi.mdiCalculatorVariant, mdi.mdiCalculatorVariantOutline],
    add: [mdi.mdiNotePlus, mdi.mdiNotePlusOutline],
    delete: [mdi.mdiDeleteForever, mdi.mdiDeleteForeverOutline],
    rename: [mdi.mdiRename, mdi.mdiRenameOutline],
    search: [mdi.mdiNoteSearch, mdi.mdiNoteSearchOutline],
    upload: [mdi.mdiUpload, mdi.mdiUploadOutline],
    download: [mdi.mdiDownload, mdi.mdiDownloadOutline],
    back: [mdi.mdiArrowLeftBold, mdi.mdiArrowLeftBoldOutline],
    maximise: [mdi.mdiFullscreen],
    minimise: [mdi.mdiFullscreenExit],
    orderByTitle: [mdi.mdiOrderAlphabeticalAscending],
    orderByTime: [mdi.mdiOrderNumericAscending],
    help: [mdi.mdiHelpCircle, mdi.mdiHelpCircleOutline],
    close: [mdi.mdiClose, mdi.mdiCloseOutline],
    about: [mdi.mdiInformation, mdi.mdiInformationOutline],
};

export function registerIcons(icons: Record<string, [string] | [string, string]>) {
    for (const [key, value] of Object.entries(icons)) {
        (iconMap as any)[key] = value;
    }
}

export type BcIconMap = {
    [K in Extract<keyof typeof iconMap, string>]: [string] | [string, string];
};

export type BcIconName = keyof BcIconMap;
export type BcIconRotate = "0" | "90" | "180" | "270";

@customElement("bc-icon")
export class BcIcon extends Component {
    @attribute accessor name: BcIconName | null = null;
    @attribute accessor path: string | null = null;
    @attribute accessor src: string | null = null;
    @attribute({ type: Boolean }) accessor outline = false;
    @attribute accessor rotate: BcIconRotate | null = null;
    @attribute accessor label: string | null = null;

    @attributeGetter({ reactive: true }) get role(): Role | null {
        return isEmpty(this.label) ? null : "img";
    }
    @attributeGetter({ reactive: true }) get ariaLabel(): string | null {
        return this.label;
    }
    @attributeGetter({ reactive: true }) get ariaHidden(): string | null {
        return isEmpty(this.label) ? "true" : null;
    }

    protected override render(): unknown {
        if (!isEmpty(this.src)) {
            return html`<svg part="svg"><use href=${this.src}></use></svg>`;
        }
        return html`<svg viewbox="0 0 24 24" part="svg">
            <path
                d=${this.path ??
                (isEmpty(this.name) || iconMap[this.name] === undefined
                    ? missing
                    : this.outline
                      ? (iconMap[this.name][1] ?? iconMap[this.name][0])
                      : iconMap[this.name][0])}
            ></path>
        </svg>`;
    }

    static styles = styles;
}
